import React, { useState, useEffect } from 'react';
import { Plus, Image, Video, Layout, Eye, Edit, Trash2, Search, Calendar } from 'lucide-react';
import { adsApiService, uploadCreativeFile } from '../services/adsApiService';

const CreativesView: React.FC = () => {
  const [creatives, setCreatives] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', startDate: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCreative, setSelectedCreative] = useState<any | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formState, setFormState] = useState({
    name: '',
    type: 'image',
    title: '',
    body: '',
    call_to_action: 'learn_more',
    destination_url: '',
    display_url: '',
    media_urls: [] as string[],
    thumbnail_url: '',
  });

  useEffect(() => {
    loadCreatives();
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await adsApiService.getAdAccounts();
      setAccounts(data);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadCreatives = async () => {
    try {
      setLoading(true);
      const data = await adsApiService.getCreatives(filters);
      setCreatives(data);
    } catch (error) {
      console.error('Error loading creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadCreatives();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = formState.type === 'video' 
      ? ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime']
      : ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(`Por favor selecciona un archivo ${formState.type === 'video' ? 'de video (MP4, MOV, AVI)' : 'de imagen (JPG, PNG, GIF)'}`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo no puede superar los 10MB');
      return;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setFilePreview(url);
  };

  const resetForm = () => {
    setFormState({ name: '', type: 'image', title: '', body: '', call_to_action: 'learn_more', destination_url: '', display_url: '', media_urls: [], thumbnail_url: '' });
    setSelectedFile(null);
    setFilePreview(null);
    setSelectedCreative(null);
  };

  const handleCreateCreative = async (e: React.FormEvent) => {
    e.preventDefault();
    if (accounts.length === 0) return;
    setUploadingFile(true);
    try {
      let mediaUrls = [...formState.media_urls];
      let thumbnailUrl = formState.thumbnail_url;
      if (selectedFile) {
        const uploadedUrl = await uploadCreativeFile(selectedFile);
        mediaUrls = [uploadedUrl];
        thumbnailUrl = uploadedUrl;
      }
      const newCreative = await adsApiService.createCreative({
        ad_account_id: accounts[0].id,
        ...formState,
        media_urls: mediaUrls,
        thumbnail_url: thumbnailUrl,
      });
      setCreatives(prev => [newCreative, ...prev]);
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      console.error('Error creating creative:', err);
      alert('Error al crear creativo: ' + (err as Error).message);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleEditCreative = (creative: any) => {
    setSelectedCreative(creative);
    setFormState(creative);
    setFilePreview(creative.thumbnail_url);
    setShowEditModal(true);
  };

  const handleUpdateCreative = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCreative) return;
    setUploadingFile(true);
    try {
      let mediaUrls = [...formState.media_urls];
      let thumbnailUrl = formState.thumbnail_url;
      if (selectedFile) {
        const uploadedUrl = await uploadCreativeFile(selectedFile);
        mediaUrls = [uploadedUrl];
        thumbnailUrl = uploadedUrl;
      }
      const updatedCreative = await adsApiService.updateCreative(selectedCreative.id, {
        ...formState,
        media_urls: mediaUrls,
        thumbnail_url: thumbnailUrl,
      });
      setCreatives(prev => prev.map(c => c.id === updatedCreative.id ? updatedCreative : c));
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      console.error('Error updating creative:', err);
      alert('Error al actualizar creativo: ' + (err as Error).message);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteCreative = (creative: any) => {
    setSelectedCreative(creative);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedCreative) return;
    try {
      await adsApiService.deleteCreative(selectedCreative.id);
      setCreatives(prev => prev.filter(c => c.id !== selectedCreative.id));
      setShowDeleteModal(false);
      setSelectedCreative(null);
    } catch (err) {
      console.error('Error deleting creative:', err);
      alert('Error al eliminar creativo: ' + (err as Error).message);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'image': return <Image size={20} className="text-blue-600" />;
      case 'video': return <Video size={20} className="text-purple-600" />;
      case 'carousel': return <Layout size={20} className="text-green-600" />;
      default: return <Image size={20} className="text-gray-600" />;
    }
  };

  const getCallToActionLabel = (cta: string) => {
    const labels: Record<string, string> = { learn_more: 'Más información', shop_now: 'Comprar ahora', sign_up: 'Registrarse', download: 'Descargar', contact_us: 'Contactar', book_now: 'Reservar' };
    return labels[cta] || cta;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="bg-gray-200 h-64 rounded-xl"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 overflow-x-hidden min-w-0">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Biblioteca de Creativos</h1>
          <p className="text-gray-600 text-sm lg:text-base">Gestiona tus anuncios y creatividades publicitarias</p>
        </div>
        <button data-tour="creatives-new" onClick={() => { resetForm(); setShowCreateModal(true); }} className="flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 text-sm lg:text-base whitespace-nowrap">
          <Plus size={20} />
          Nuevo Creativo
        </button>
      </div>

      <form onSubmit={handleFilterSubmit} className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Buscar por nombre..." className="w-full pl-10 pr-4 py-2 border rounded-lg" />
        </div>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full pl-10 pr-4 py-2 border rounded-lg" />
        </div>
        <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg">Filtrar</button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
        {creatives.map((creative) => (
          <div key={creative.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
            <div className="aspect-video bg-gray-100 relative group">
              {creative.thumbnail_url ? (
                creative.type === 'video' ? <video src={creative.media_urls?.[0] || creative.thumbnail_url} className="w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                : <img src={creative.thumbnail_url} alt={creative.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Image size={48} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm opacity-75">Vista previa del creativo</p>
                  </div>
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                <button onClick={(e) => { e.stopPropagation(); if (creative.thumbnail_url) window.open(creative.thumbnail_url, '_blank'); }} className="opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-90 text-gray-900 px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-opacity-100 text-sm">
                  <Eye size={16} />
                  Vista completa
                </button>
              </div>
              <div className="absolute top-3 left-3 bg-white bg-opacity-90 p-2 rounded-lg">{getTypeIcon(creative.type)}</div>
              <div className="absolute top-3 right-3 flex gap-2">
                <button title="Editar creativo" onClick={() => handleEditCreative(creative)} className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"><Edit size={16} /></button>
                <button title="Eliminar creativo" onClick={() => handleDeleteCreative(creative)} className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-2 mb-4">
                <p className="font-medium text-gray-900 text-sm lg:text-base truncate">{creative.title}</p>
                <p className="text-gray-600 text-xs lg:text-sm line-clamp-2">{creative.body}</p>
              </div>
            </div>
            <div className="border-t border-gray-100">
              <button onClick={(e) => { e.stopPropagation(); if (creative.destination_url) window.open(creative.destination_url, '_blank'); }} className="w-full px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors font-semibold text-center text-sm">
                {getCallToActionLabel(creative.call_to_action)}
              </button>
              <div className="px-4 py-2 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
                <span className="truncate">📊 Disponible para campañas</span>
                <span className="whitespace-nowrap">{new Date(creative.created_at).toLocaleDateString('es-ES')}</span>
              </div>
            </div>
          </div>
        ))}
        <div onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all cursor-pointer group">
          <div className="aspect-video flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-100 transition-colors">
                <Plus className="text-gray-400 group-hover:text-purple-600" size={32} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 text-sm lg:text-base">Crear Nuevo Creativo</h3>
              <p className="text-gray-500 text-xs lg:text-sm">Diseña tu próximo anuncio</p>
              <p className="text-purple-600 text-xs mt-1 font-medium hidden lg:block">Úsalo en múltiples campañas</p>
            </div>
          </div>
        </div>
      </div>

      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{showEditModal ? 'Editar Creativo' : 'Nuevo Creativo'}</h2>
                <button onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-full">×</button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <form id="creative-form" onSubmit={showEditModal ? handleUpdateCreative : handleCreateCreative} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del creativo</label>
                    <input type="text" required value={formState.name} onChange={(e) => setFormState({...formState, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Ej: Anuncio Pinturas Abstractas Enero" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de creativo</label>
                    <select value={formState.type} onChange={(e) => setFormState({...formState, type: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg">
                      <option value="image">🖼️ Imagen única</option>
                      <option value="video">🎥 Video</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Título del anuncio</label>
                    <input type="text" value={formState.title} onChange={(e) => setFormState({...formState, title: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Ej: Arte Original Hecho a Mano" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Texto del anuncio</label>
                    <textarea rows={4} value={formState.body} onChange={(e) => setFormState({...formState, body: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Ej: Transforma tu hogar con arte original." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Llamada a la acción</label>
                    <select value={formState.call_to_action} onChange={(e) => setFormState({...formState, call_to_action: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg">
                      <option value="shop_now">🛒 Comprar ahora</option>
                      <option value="learn_more">👀 Más información</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL de destino</label>
                    <input type="url" value={formState.destination_url} onChange={(e) => setFormState({...formState, destination_url: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="https://soartistic.com/tu-perfil" />
                  </div>
                </form>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">📸 Subir tu obra</h4>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <input type="file" accept={formState.type === 'video' ? 'video/*' : 'image/*'} onChange={handleFileSelect} className="hidden" id="desktop-upload" />
                      <label htmlFor="desktop-upload" className="cursor-pointer block">
                        <Image className="mx-auto text-gray-400 mb-2" size={32} />
                        <p className="text-gray-600 text-sm">Sube la obra que quieres promocionar</p>
                      </label>
                    </div>
                    {filePreview && (
                      <div className="mt-4">
                        <img src={filePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-4 pt-6 mt-6 border-t border-gray-200">
                <button type="button" onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }} disabled={uploadingFile} className="flex-1 px-6 py-3 border rounded-lg">Cancelar</button>
                <button type="submit" form="creative-form" disabled={uploadingFile} className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg">
                  {uploadingFile ? 'Guardando...' : (showEditModal ? 'Guardar Cambios' : 'Crear Creativo')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Confirmar Eliminación</h2>
              <p>¿Estás seguro de que quieres eliminar este creativo?</p>
              <div className="flex justify-end gap-4 mt-6">
                <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border rounded-lg">Cancelar</button>
                <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg">Eliminar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativesView;
